// features/invoice/invoice.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import i18n from '../../../translation/i18n';
import {
  getBankDetailsApi,
  generateOTPApi,
  saveBankDetailsApi,
  calculateInvoiceApi,
  createInvoiceApi,
  createPreliminaryInvoiceApi,
  createDraftInvoiceApi,
  getAllInvoicesApi,
  getInvoiceDetailForEditApi,
  getDraftInvoiceDetailApi,
  getTotalCalculationApi,
  BankDetails,
  SaveBankDetailsRequest,
  InvoiceCalculationsPayload,
  InvoiceCalculationsResponse,
  TotalCalculationBreakdown,
  CreateInvoicePayload,
  CreateInvoiceResponse,
  CreateDraftInvoicePayload,
  CreateDraftInvoiceResponse,
  GetAllInvoicesParams,
  InvoiceItem,
  TotalCalculationResponse
} from './invoice.service';
import { logOut } from '../../../redux/auth/authSlice';

/* ----------------------------- Thunks ---------------------------------- */

export const getBankDetails = createAsyncThunk(
  'invoice/getBankDetails',
  async (_, thunkAPI) => {
    try {
      return await getBankDetailsApi();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const generateOTP = createAsyncThunk(
  'invoice/generateOTP',
  async (lang: string = 'en', thunkAPI) => {
    try {
      return await generateOTPApi(lang);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const saveBankDetails = createAsyncThunk(
  'invoice/saveBankDetails',
  async (payload: SaveBankDetailsRequest, thunkAPI) => {
    try {
      return await saveBankDetailsApi(payload);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        error.toString();
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const calculateInvoice = createAsyncThunk(
  'invoice/calculateInvoice',
  async (payload: InvoiceCalculationsPayload, thunkAPI) => {
    try {
      return await calculateInvoiceApi(payload);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to calculate invoice';
      return thunkAPI.rejectWithValue(message);
    }
  } 
);

export const createInvoice = createAsyncThunk(
  'invoice/createInvoice',
  async (payload: CreateInvoicePayload, thunkAPI) => {
    try {
      const response = await createInvoiceApi(payload);
      return response;
    } catch (error: any) {
      // Check for 401 Unauthorized error
      if (error?.response?.status === 401) {
        const errorMessage = i18n.t('UPDATE_EMAIL_TO_CONTINUE') as string;
        return thunkAPI.rejectWithValue(errorMessage);
      }

      const statusCode = error?.response?.status ?? error?.response?.data?.statusCode;
      const backendMessage = error?.response?.data?.message || '';

      // Map the Fortnox "invalid social security number" 500 error to a
      // translated, user-friendly message (en-us / sv-se).
      if (statusCode === 500 && /Ej giltigt personnummer/i.test(backendMessage)) {
        return thunkAPI.rejectWithValue(i18n.t('INVALID_SOCIAL_SECURITY_NUMBER') as string);
      }

      const message =
        backendMessage ||
        error?.message ||
        'Failed to create invoice';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getTotalCalculation = createAsyncThunk(
  'invoice/getTotalCalculation',
  async (payload: CreateInvoicePayload, thunkAPI) => {
    try {
      const response = await getTotalCalculationApi(payload);
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to calculate total';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createPreliminaryInvoice = createAsyncThunk(
  'invoice/createPreliminaryInvoice',
  async (payload: CreateInvoicePayload, thunkAPI) => {
    try {
      const response = await createPreliminaryInvoiceApi(payload);
      return response;
    } catch (error: any) {
      // Check for 401 Unauthorized error
      if (error?.response?.status === 401) {
        const errorMessage = i18n.t('UPDATE_EMAIL_TO_CONTINUE') as string;
        return thunkAPI.rejectWithValue(errorMessage);
      }
      
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create preliminary invoice';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createDraftInvoice = createAsyncThunk(
  'invoice/createDraftInvoice',
  async (payload: CreateDraftInvoicePayload, thunkAPI) => {
    try {
      const response = await createDraftInvoiceApi(payload);
      return response;
    } catch (error: any) {
      // Check for 401 Unauthorized error
      if (error?.response?.status === 401) {
        const errorMessage = i18n.t('UPDATE_EMAIL_TO_CONTINUE') as string;
        return thunkAPI.rejectWithValue(errorMessage);
      }
      
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create draft invoice';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getAllInvoices = createAsyncThunk(
  'invoice/getAllInvoices',
  async (params: GetAllInvoicesParams, thunkAPI) => {
    try {
      return await getAllInvoicesApi(params);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch invoices';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getSelectedInvoiceDetails = createAsyncThunk(
  'invoice/getSelectedInvoiceDetails',
  async (invoiceNumber: string, thunkAPI) => {
    try {
      // Use new API endpoint for getting invoice details for edit
      const invoiceDetail = await getInvoiceDetailForEditApi(invoiceNumber);
      
      // Return in the format expected by the component
      return {
        data: [invoiceDetail], // Wrap in array to match existing format
        totalCount: 1
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch invoice details';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const getDraftInvoiceDetails = createAsyncThunk(
  'invoice/getDraftInvoiceDetails',
  async ({ invoiceId, lang }: { invoiceId: string; lang?: string }, thunkAPI) => {
    try {
      // Use draft invoice API endpoint (same response format as invoice detail for edit)
      const invoiceDetail = await getDraftInvoiceDetailApi(invoiceId, lang || 'en-us');
      
      // Return in the format expected by the component
      return {
        data: [invoiceDetail], // Wrap in array to match existing format
        totalCount: 1
      };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to fetch draft invoice details';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

/* ----------------------------- State ---------------------------------- */

type InvoiceState = {
  bankDetails: BankDetails[] | null;
  calculationData: TotalCalculationBreakdown | null;
  totalCalculationData: TotalCalculationResponse | null;
  createdInvoice: CreateInvoiceResponse | null;
  preliminaryInvoice: string | null; // Changed from Blob to string (object URL)
  draftInvoice: CreateDraftInvoiceResponse | null;
  allInvoices: InvoiceItem[];
  totalInvoicesCount: number;
  selectedInvoiceDetails: InvoiceItem | null;

  loading: boolean;       // loading for getBankDetails + calculation
  otpLoading: boolean;
  saveLoading: boolean;
  createLoading: boolean;
  preliminaryLoading: boolean;
  draftLoading: boolean;
  invoicesLoading: boolean;
  selectedInvoiceLoading: boolean;
  totalCalculationLoading: boolean;

  error: string | null;
  message: string | null;
};

const initialState: InvoiceState = {
  bankDetails: null,
  calculationData: null,
  totalCalculationData: null,
  createdInvoice: null,
  preliminaryInvoice: null,
  draftInvoice: null,
  allInvoices: [],
  totalInvoicesCount: 0,
  selectedInvoiceDetails: null,

  loading: false,
  otpLoading: false,
  saveLoading: false,
  createLoading: false,
  preliminaryLoading: false,
  draftLoading: false,
  invoicesLoading: false,
  selectedInvoiceLoading: false,
  totalCalculationLoading: false,

  error: null,
  message: null,
};

/* ----------------------------- Slice ---------------------------------- */

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    clearInvoiceData: state => {
      state.bankDetails = null;
      state.error = null;
      state.message = null;
    },
    clearInvoiceCalculationData: state => {
      state.calculationData = null;
      state.error = null;
      state.message = null;
    },
    clearError: state => {
      state.error = null;
    },
    clearMessage: state => {
      state.message = null;
    },
    resetInvoiceState: state => {
      state.selectedInvoiceDetails = null;
      state.calculationData = null;
      state.totalCalculationData = null;
      state.createdInvoice = null;
      state.preliminaryInvoice = null;
      state.draftInvoice = null;
      state.error = null;
      state.message = null;
    }
  },

  extraReducers: builder => {
    builder
      /* ----------------- Get Bank Details ----------------- */
      .addCase(getBankDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBankDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.bankDetails = action.payload;
      })
      .addCase(getBankDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.bankDetails = null;
      })

      /* ----------------- Generate OTP ----------------- */
      .addCase(generateOTP.pending, state => {
        state.otpLoading = true;
        state.error = null;
      })
      .addCase(generateOTP.fulfilled, (state) => {
        state.otpLoading = false;
        state.message = i18n.t('OTP sent successfully to your email!') as string;
        toast.success(i18n.t('OTP sent successfully to your email!') as string);
      })
      .addCase(generateOTP.rejected, (state, action) => {
        state.otpLoading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string);
      })

      /* ----------------- Save Bank Details ----------------- */
      .addCase(saveBankDetails.pending, state => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(saveBankDetails.fulfilled, (state) => {
        state.saveLoading = false;
        state.message = i18n.t('Bank details saved successfully!') as string;
        toast.success(i18n.t('Bank details saved successfully!') as string);
      })
      .addCase(saveBankDetails.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload as string;
        toast.error(i18n.t(action.payload as string) as string);
      })

      /* ----------------- Calculate Invoice (Background - No Loader) ----------------- */
      .addCase(calculateInvoice.pending, state => {
        // Don't set loading state to avoid blocking UI
        state.error = null;
      })
      .addCase(calculateInvoice.fulfilled, (state, action) => {
        // Update calculation data without loading state
        state.calculationData = action.payload;
        state.message = 'Invoice calculations completed successfully!';
      })
      .addCase(calculateInvoice.rejected, (state, action) => {
        // Log error but don't block UI
        state.error = action.payload as string;
      })

      /* ----------------- Create Invoice ----------------- */
      .addCase(createInvoice.pending, state => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createdInvoice = action.payload;
        
        // Only show success toast when statusCode is 200 or 201
        if (action.payload?.statusCode === 200 || action.payload?.statusCode === 201) {
          state.message = i18n.t('INVOICE_CREATED_SUCCESSFULLY') as string;
          toast.success(i18n.t('INVOICE_CREATED_SUCCESSFULLY') as string);
        }
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
        toast.error(i18n.t(action.payload as string) as string);
      })

      /* ----------------- Get Total Calculation ----------------- */
      .addCase(getTotalCalculation.pending, state => {
        state.totalCalculationLoading = true;
        state.error = null;
      })
      .addCase(getTotalCalculation.fulfilled, (state, action) => {
        state.totalCalculationLoading = false;
        state.totalCalculationData = action.payload;
        state.message = 'Total calculation completed successfully!';
      })
      .addCase(getTotalCalculation.rejected, (state, action) => {
        state.totalCalculationLoading = false;
        state.error = action.payload as string;
      })

      /* ----------------- Create Preliminary Invoice ----------------- */
      .addCase(createPreliminaryInvoice.pending, state => {
        state.preliminaryLoading = true;
        state.error = null;
      })
      .addCase(createPreliminaryInvoice.fulfilled, (state, action) => {
        state.preliminaryLoading = false;
        // Convert Blob to object URL for serializable storage
        if (action.payload instanceof Blob) {
          state.preliminaryInvoice = URL.createObjectURL(action.payload);
        } else {
          state.preliminaryInvoice = action.payload;
        }
        state.message = i18n.t('PRELIMINARY_INVOICE_CREATED_SUCCESSFULLY') as string;
        // No toast - preview will open automatically
      })
      .addCase(createPreliminaryInvoice.rejected, (state, action) => {
        state.preliminaryLoading = false;
        state.error = action.payload as string;
        toast.error(i18n.t(action.payload as string) as string);
      })

      /* ----------------- Create Draft Invoice ----------------- */
      .addCase(createDraftInvoice.pending, state => {
        state.draftLoading = true;
        state.error = null;
      })
      .addCase(createDraftInvoice.fulfilled, (state, action) => {
        state.draftLoading = false;
        state.draftInvoice = action.payload;
        state.message = i18n.t('DRAFT_INVOICE_SAVED_SUCCESSFULLY') as string;
        // toast.success(i18n.t('DRAFT_INVOICE_SAVED_SUCCESSFULLY') as string);
      })
      .addCase(createDraftInvoice.rejected, (state, action) => {
        state.draftLoading = false;
        state.error = action.payload as string;
        toast.error(i18n.t(action.payload as string) as string);
      })

      /* ----------------- Get All Invoices ----------------- */
      .addCase(getAllInvoices.pending, state => {
        state.invoicesLoading = true;
        state.error = null;
      })
      .addCase(getAllInvoices.fulfilled, (state, action) => {
        state.invoicesLoading = false;
        state.allInvoices = action.payload.data;
        state.totalInvoicesCount = action.payload.totalCount;
      })
      .addCase(getAllInvoices.rejected, (state, action) => {
        state.invoicesLoading = false;
        state.error = action.payload as string;
        state.allInvoices = [];
        state.totalInvoicesCount = 0;
      })

      /* ----------------- Get Selected Invoice Details ----------------- */
      .addCase(getSelectedInvoiceDetails.pending, (state) => {
        state.selectedInvoiceLoading = true;
        // Clear old invoice data while loading new data to prevent showing stale data
        state.selectedInvoiceDetails = null;
      })
      .addCase(getSelectedInvoiceDetails.fulfilled, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.selectedInvoiceDetails = action.payload.data[0] || null;
      })
      .addCase(getSelectedInvoiceDetails.rejected, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.error = action.payload as string;
        state.selectedInvoiceDetails = null;
      })

      /* ----------------- Get Draft Invoice Details ----------------- */
      .addCase(getDraftInvoiceDetails.pending, (state) => {
        state.selectedInvoiceLoading = true;
        // Clear old invoice data while loading new data to prevent showing stale data
        state.selectedInvoiceDetails = null;
      })
      .addCase(getDraftInvoiceDetails.fulfilled, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.selectedInvoiceDetails = action.payload.data[0] || null;
      })
      .addCase(getDraftInvoiceDetails.rejected, (state, action) => {
        state.selectedInvoiceLoading = false;
        state.error = action.payload as string;
        state.selectedInvoiceDetails = null;
      })

      /* ----------------- Reset on logout ----------------- */
      .addCase(logOut, () => initialState);
  }
});

/* ----------------------------- Exports ---------------------------------- */

export const {
  clearInvoiceData,
  clearInvoiceCalculationData,
  clearError,
  clearMessage,
  resetInvoiceState
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
